import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { IFilterBody, IPagination, IApiResponseDto, IPostType, ITopic, IPolitician } from '../../../../helpers/general.interface';
import { TwitterService } from '../../Services/twitter.service';
import { IGetTwitterPostsData } from '../../Models/ITwitterPost';
import { faEdit, faEye, faComments, faExternalLink, faSearch, faSave, faCancel } from '@fortawesome/free-solid-svg-icons';
import { TwitterPostDetailsComponent } from '../twitter-post-details/twitter-post-details.component';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { ITwitterPostDetailsPutDto } from '../../Models/ITwitterPostDetailsPutDto';
import { SpecialDDLValuesEnum } from '../../../../shared/enums/politician-ddl-values.enum';
import { SpecialDDLOptionsEnum } from '../../../../shared/enums/special-ddl-options.enum';
import { PostTypeEnum } from '../../../../shared/enums/post-type.enum';


@Component({
  selector: 'app-twitter-posts',
  templateUrl: './twitter-posts.component.html',
  styleUrls: ['./twitter-posts.component.scss']
})
export class TwitterPostsComponent implements OnInit {
  filtersForm!: FormGroup;
  hashtags: string[] = [];
  politicians: IPolitician[] = [];
  attackedPoliticians: IPolitician[] = [];
  types: IPostType[] = [];
  topics: ITopic[] = [];

  inputValue?: string;
  options: string[] = [];
  filteredAttackedPoliticians: any[] = [];


  posts: IGetTwitterPostsData[] = [];
  isDataLoading: boolean = false;
  totalCount: number = 40;
  pageSize: number = 10;
  pageNumber: number = 0;
  pageSizeOptions = [10, 25, 100];
  sortBy: string = 'date'; // Default sort field
  sortDir: string = 'desc'; // Default sort direction
  searchVal: string = ''; // Default search value

  // Filter constants
  allPoliticianItem = { id: String(SpecialDDLValuesEnum.ALL), name: SpecialDDLOptionsEnum.ALL };
  unspecifiedPoliticianItem = { id: String(SpecialDDLValuesEnum.UNSPECIFIED), name: SpecialDDLOptionsEnum.UNSPECIFIED };

  allTopicItem = { id: SpecialDDLValuesEnum.ALL, name: SpecialDDLOptionsEnum.ALL };
  unSpecifiedTopicItem = { id: SpecialDDLValuesEnum.UNSPECIFIED, name: SpecialDDLOptionsEnum.UNSPECIFIED };

  allPostTypeItem = { id: SpecialDDLValuesEnum.ALL, type: SpecialDDLOptionsEnum.ALL };
  unPostTypeItem = { id: SpecialDDLValuesEnum.UNSPECIFIED, type: SpecialDDLOptionsEnum.UNSPECIFIED };

  // icons
  faEdit = faEdit;
  faView = faEye;
  faComments = faComments;
  faExternalLink = faExternalLink;
  faSearch = faSearch;
  faSave = faSave;
  faCancel = faCancel;

  // Edit cache for inline editing
  editCache: { [key: string]: { edit: boolean; data: IGetTwitterPostsData } } = {};


  constructor(
    private fb: FormBuilder,
    private twitterService: TwitterService,
    private message: NzMessageService,
    private drawerService: NzDrawerService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();
    this.getAllTwitterPosts();
  }

  private initForm(): void {
    this.filtersForm = this.fb.group({
      politician: [SpecialDDLValuesEnum.ALL.toString()],
      hashtag: [SpecialDDLOptionsEnum.ALL],
      attackedPolitician: [SpecialDDLValuesEnum.ALL.toString()],
      topic: [SpecialDDLValuesEnum.ALL],
      postType: [SpecialDDLValuesEnum.ALL],
      searchBox: ['']
    });
  }

  private loadDropdowns(): void {
    this.getPoliticians();
    this.getHashtags();
    this.getAttackedPoliticians();
    this.getPostTypes();
    this.getTopics();
  }


  updateEditCache(): void {
    this.posts.forEach(post => {
      if (!this.editCache[post.id]) {
        const copy = { ...post };

        // Normalize nulls → UNSPECIFIED for editing
        copy.attackedPolitician = copy.attackedPolitician || SpecialDDLOptionsEnum.UNSPECIFIED;

        if (!copy.type) {
          copy.type = { id: SpecialDDLValuesEnum.UNSPECIFIED, type: SpecialDDLOptionsEnum.UNSPECIFIED };
        }
        if (!copy.topic) {
          copy.topic = { id: SpecialDDLValuesEnum.UNSPECIFIED, name: SpecialDDLOptionsEnum.UNSPECIFIED };
        }

        this.editCache[post.id] = {
          edit: false,
          data: copy
        };
      }
    });
  }

  startEdit(id: string): void {
    this.editCache[id].edit = true;
  }

  cancelEdit(id: string): void {
    const original = this.posts.find(p => p.id === id);
    if (original) {
      this.editCache[id] = {
        edit: false,
        data: { ...original }
      };
      // Re-normalize
      const data = this.editCache[id].data;
      data.attackedPolitician = data.attackedPolitician || SpecialDDLOptionsEnum.UNSPECIFIED;
      if (!data.type?.id) data.type = { id: SpecialDDLValuesEnum.UNSPECIFIED, type: SpecialDDLOptionsEnum.UNSPECIFIED };
      if (!data.topic?.id) data.topic = { id: SpecialDDLValuesEnum.UNSPECIFIED, name: SpecialDDLOptionsEnum.UNSPECIFIED };
    }
  }



  getAllTwitterPosts(paginationParams?: IPagination): void {
    this.isDataLoading = true;

    if (!paginationParams) {
      paginationParams = {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      };
    }

    const politicianId = this.filtersForm.value.politician;
    const hashtag = this.filtersForm.value.hashtag;
    const attackedPolitician = this.filtersForm.value.attackedPolitician;
    const topicId = this.filtersForm.value.topic;
    const postTypeId = this.filtersForm.value.postType;
    const searchValue = this.filtersForm.value.searchBox;

    const filtersBody = this.populateFilterObj(
      politicianId,
      hashtag,
      attackedPolitician,
      topicId,
      postTypeId
    );

    this.twitterService.getAllPosts(
      paginationParams,
      this.sortBy,
      this.sortDir,
      searchValue,
      filtersBody
    ).subscribe(
      (response) => {
        this.posts = response.content;
        this.pageNumber = response.pageable.pageNumber;
        this.pageSize = response.pageable.pageSize;
        this.totalCount = response.totalElements;
        this.isDataLoading = false;
        this.pageNumber = paginationParams.pageNumber;
        this.pageSize = paginationParams.pageSize;

        this.updateEditCache();//update cache after loading posts
      },
      (error) => {
        console.error('Error loading posts:', error);
        this.isDataLoading = false;
        this.message.error('Ndodhi një gabim gjatë ngarkimit të të dhënave');
      }
    );
  }

  saveEdit(id: string): void {
    const cacheData = this.editCache[id].data;

    const dto: ITwitterPostDetailsPutDto = {
      id,
      attackedPolitician:
        cacheData.attackedPolitician === SpecialDDLOptionsEnum.UNSPECIFIED
          ? null
          : cacheData.attackedPolitician,
      typeId:
        cacheData.type?.id === SpecialDDLValuesEnum.UNSPECIFIED
          ? null
          : cacheData.type!.id,
      topicId:
        cacheData.topic?.id === SpecialDDLValuesEnum.UNSPECIFIED
          ? null
          : cacheData.topic!.id
    };

    this.twitterService.updatePostDetails(dto).subscribe({
      next: () => {
        const index = this.posts.findIndex(p => p.id === id);
        if (index !== -1) {
          this.posts[index].attackedPolitician = dto.attackedPolitician;
          this.posts[index].type = dto.typeId
            ? this.types.find(t => t.id === dto.typeId) || null
            : null;
          this.posts[index].topic = dto.topicId
            ? this.topics.find(t => t.id === dto.topicId) || null
            : null;
        }

        // Add new politician if created
        if (dto.attackedPolitician && !this.attackedPoliticians.find(p => p.name === dto.attackedPolitician)) {
          this.attackedPoliticians.push({ id: dto.attackedPolitician, name: dto.attackedPolitician });
        }

        this.editCache[id].edit = false;
        this.message.success('Post updated successfully');
      },
      error: () => {
        this.message.error('Failed to update post');
        this.cancelEdit(id);
      }
    });
  }

  private populateFilterObj(politicianId: string, hashtag: string, attackedPolitician: string, topic: number, postType: number): IFilterBody[] {
    let filtersBody: IFilterBody[] = [];

    if (politicianId != null && politicianId != undefined && politicianId != "0")
      filtersBody.push({ propertyName: 'politicianId', value: politicianId });

    if (hashtag != null && hashtag != undefined && hashtag != SpecialDDLOptionsEnum.ALL)
      filtersBody.push({ propertyName: 'mainHashtag', value: hashtag.replace('#', '').toString() });

    if (attackedPolitician != null && attackedPolitician != undefined && attackedPolitician != SpecialDDLValuesEnum.ALL.toString())
      if (attackedPolitician != "-1")
        filtersBody.push({ propertyName: 'attackedPolitician', value: attackedPolitician.toString() });
      else
        filtersBody.push({ propertyName: 'attackedPolitician', value: null });

    if (topic != null && topic != undefined && topic != SpecialDDLValuesEnum.ALL)
      if (topic != -1)
        filtersBody.push({ propertyName: 'topic', value: topic.toString() });
      else
        filtersBody.push({ propertyName: 'topic', value: null });

    if (postType != null && postType != undefined && postType != SpecialDDLValuesEnum.ALL)
      if (postType != -1)
        filtersBody.push({ propertyName: 'postType', value: postType.toString() });
      else
        filtersBody.push({ propertyName: 'postType', value: null });

    return filtersBody;
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    if (params.sort && params.sort.length > 0) {
      const sort = params.sort[0];
      this.sortBy = sort.key;
      this.sortDir = sort.value === 'ascend' ? 'asc' : 'desc';
    }

    let paginationParams: IPagination = {
      pageNumber: params.pageIndex,
      pageSize: params.pageSize
    };

    this.getAllTwitterPosts(paginationParams);
  }

  public search(): void {
    let paginationParams: IPagination = {
      pageNumber: 0,
      pageSize: this.pageSize
    };

    this.getAllTwitterPosts(paginationParams);
  }

  public getPoliticians() {
    this.twitterService.getPoliticians().subscribe(
      (data: any) => {
        this.politicians = data;
        this.politicians.unshift(this.allPoliticianItem);
      },
      error => {
        console.error('Error loading politicians', error);
        this.message.error('Error loading politicians');
      }
    );
  }

  public getHashtags() {
    this.twitterService.getHashtags().subscribe(
      (data: string[]) => {
        this.hashtags = data;
        this.hashtags.unshift(SpecialDDLOptionsEnum.ALL);
      },
      error => {
        console.error('Error loading Hashtags', error);
        this.message.error('Error loading Hashtags');
      }
    );
  }

  public getAttackedPoliticians() {
    this.twitterService.getAttackedPoliticians().subscribe(
      (data: IPolitician[]) => {
        this.attackedPoliticians = data;
        this.attackedPoliticians.unshift(this.allPoliticianItem, this.unspecifiedPoliticianItem);
      },
      error => {
        console.error('Error loading attacked politicians', error);
        this.message.error('Error loading attacked politicians');
      }
    );
  }

  public getPostTypes() {
    this.twitterService.getPostTypes().subscribe(
      (data: IPostType[]) => {
        this.types = data;
        this.types.unshift(this.allPostTypeItem, this.unPostTypeItem);
      },
      error => {
        console.error('Error loading types', error);
        this.message.error('Error loading types');
      }
    );
  }

  public getTopics() {
    this.twitterService.getPostTopics().subscribe(
      (data: ITopic[]) => {
        this.topics = data;
        this.topics.unshift(this.allTopicItem, this.unSpecifiedTopicItem);
      },
      error => {
        console.error('Error loading topics', error);
        this.message.error('Error loading topics');
      }
    );
  }

  openPostDetails(postId: string): void {
    this.drawerService.create<TwitterPostDetailsComponent>({
      nzTitle: 'Twitter Post Details',
      nzContent: TwitterPostDetailsComponent,
      nzContentParams: {
        postId
      },
      nzWidth: '40%',
      nzClosable: true,
      nzMaskClosable: true
    });
  }


  onAttackedPoliticianInput(value: string): void {
    // value is already the string!
    if (!value?.trim()) {
      this.filteredAttackedPoliticians = this.attackedPoliticians.filter(
        p => p.name !== SpecialDDLOptionsEnum.ALL && p.name !== SpecialDDLOptionsEnum.UNSPECIFIED
      );
      return;
    }

    const lower = value.toLowerCase();
    const matches = this.attackedPoliticians.filter(p =>
      p.name.toLowerCase().includes(lower) &&
      p.name !== SpecialDDLOptionsEnum.ALL &&
      p.name !== SpecialDDLOptionsEnum.UNSPECIFIED
    );

    const exactMatch = matches.some(p => p.name.toLowerCase() === lower);
    if (!exactMatch && value.trim()) {
      this.filteredAttackedPoliticians = [{ id: 'new', name: value }, ...matches];
    } else {
      this.filteredAttackedPoliticians = matches;
    }
  }

  onAttackedPoliticianChange(value: string, postId: string): void {
    if (value && value !== SpecialDDLOptionsEnum.UNSPECIFIED) {
      this.editCache[postId].data.type!.id = PostTypeEnum.POLITICAL_ATTACK_TYPE_ID; // Force Attack type
    }
  }

  onPostTypeChange(value: number, postId: string): void {
    if (value !== PostTypeEnum.POLITICAL_ATTACK_TYPE_ID) {
      this.editCache[postId].data.attackedPolitician = SpecialDDLOptionsEnum.UNSPECIFIED;
    }
  }



}